import * as React from 'react';
import { useMemo } from 'react';
import { Avatar } from '../../primitives/Avatar/Avatar';
import { Badge } from '../../primitives/Badge/Badge';
import { GovernanceTag } from '../GovernanceTag/GovernanceTag';
import { TrustBadge } from '../TrustBadge/TrustBadge';
import type { Message } from '../_types';
import {
  row as rowCls,
  column as columnCls,
  metaLine as metaLineCls,
  authorName as authorNameCls,
  authorNameButton as authorNameButtonCls,
  authorRole as authorRoleCls,
  timestamp as timestampCls,
  separator as separatorCls,
  bubble as bubbleCls,
  bubbleScope as bubbleScopeCls,
  watermark as watermarkCls,
  systemLine as systemLineCls,
  systemTimestamp as systemTimestampCls,
} from './MessageBubble.css';

export type MessageBubbleDensity = 'comfortable' | 'compact';

export interface MessageBubbleProps extends Omit<React.HTMLAttributes<HTMLElement>, 'children'> {
  message: Message;
  density?: MessageBubbleDensity;
  isSelf?: boolean;
  showMeta?: boolean;
  /** Override avatar background — for tenant-tinted human avatars. */
  avatarBg?: string;
  onOpenProfile?: (memberId: string) => void;
  /** React 19 ref-as-prop. */
  ref?: React.Ref<HTMLElement>;
}

/** Map message variant → avatar ring tone. */
function ringToneFor(variant: Message['variant']): 'none' | 'blue' | 'purple' | 'teal' {
  if (variant === 'agent') return 'blue';
  if (variant === 'smith' || variant === 'mira') return 'purple';
  if (variant === 'workstreamAgent') return 'teal';
  return 'none';
}

/** Map message variant → "Agent" badge tone in the metaLine. */
function showsAgentBadge(variant: Message['variant']): boolean {
  return variant === 'agent' || variant === 'smith' || variant === 'mira';
}

/** Simple inline markdown renderer — bold, italic, inline code. */
function renderInlineMarkdown(text: string): React.ReactNode {
  const tokens: React.ReactNode[] = [];
  let key = 0;
  const push = (node: React.ReactNode) =>
    tokens.push(<React.Fragment key={key++}>{node}</React.Fragment>);
  const re = /(\*\*[^*]+\*\*|\*[^*]+\*|_[^_]+_|`[^`]+`)/g;
  let lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text))) {
    if (m.index > lastIndex) push(text.slice(lastIndex, m.index));
    const t = m[0];
    if (t.startsWith('**') && t.endsWith('**')) {
      push(<strong>{t.slice(2, -2)}</strong>);
    } else if (t.startsWith('*') && t.endsWith('*')) {
      push(<em>{t.slice(1, -1)}</em>);
    } else if (t.startsWith('_') && t.endsWith('_')) {
      push(<em>{t.slice(1, -1)}</em>);
    } else if (t.startsWith('`') && t.endsWith('`')) {
      push(<code>{t.slice(1, -1)}</code>);
    }
    lastIndex = m.index + t.length;
  }
  if (lastIndex < text.length) push(text.slice(lastIndex));
  return tokens;
}

/**
 * MessageBubble — a single chat row in the prototype layout: avatar
 * (size lg) on the left, header (name + agent badge + timestamp) above
 * a bubble with a 3px sender-coded LEFT border. The bubble's interior
 * uses asymmetric padding (24px right vs 14px left) to optically
 * balance the coloured stripe and a `calc(100% - 24px)` max-width so
 * it never stretches edge-to-edge inside the chat column.
 *
 * Validated 2026-05-02 against the BG Private workspace prototype at
 * `http://localhost:8000/`.
 */
export function MessageBubble({
  message,
  density = 'comfortable',
  isSelf = false,
  showMeta = true,
  avatarBg,
  onOpenProfile,
  ref,
  className,
  ...rest
}: MessageBubbleProps) {
  const variant = message.variant;
  const isSystem = variant === 'workstreamAgent';
  const isAgent = showsAgentBadge(variant);

  const content = useMemo(() => renderInlineMarkdown(message.text), [message.text]);

  if (isSystem) {
    const cls = [rowCls({ density, isSelf: false, workstreamAgent: true }), className]
      .filter(Boolean)
      .join(' ');
    return (
      <article
        ref={ref}
        role="status"
        aria-label={`System: ${message.text}`}
        className={cls}
        {...rest}
      >
        <div className={systemLineCls}>
          <span>{message.text}</span>
          <span className={systemTimestampCls}>· {message.time}</span>
        </div>
      </article>
    );
  }

  const cls = [rowCls({ density, isSelf, workstreamAgent: false }), className]
    .filter(Boolean)
    .join(' ');
  const bubbleClassName = [bubbleCls({ density, isSelf, variant }), bubbleScopeCls]
    .filter(Boolean)
    .join(' ');

  return (
    <article
      ref={ref}
      className={cls}
      aria-label={`${message.memberName} at ${message.time}`}
      {...rest}
    >
      <Avatar
        name={message.memberName}
        size="lg"
        ringTone={ringToneFor(variant)}
        style={avatarBg ? { background: avatarBg } : undefined}
      />
      <div className={columnCls}>
        {showMeta && (
          <div className={metaLineCls({ isSelf })}>
            {onOpenProfile ? (
              <button
                type="button"
                className={authorNameButtonCls}
                onClick={() => onOpenProfile(message.memberId)}
              >
                {message.memberName}
              </button>
            ) : (
              <span className={authorNameCls}>{message.memberName}</span>
            )}
            {isAgent && (
              <Badge tone="agent" size="sm">
                Agent
              </Badge>
            )}
            {message.tag && (
              <>
                <span className={separatorCls} aria-hidden="true">
                  ·
                </span>
                <GovernanceTag domain={message.tag} size="sm" />
              </>
            )}
            {variant === 'mira' && message.trustLevel && (
              <>
                <span className={separatorCls} aria-hidden="true">
                  ·
                </span>
                <TrustBadge level={message.trustLevel} size="sm" />
              </>
            )}
            {message.memberRole && message.memberRole !== message.memberName && (
              <span className={authorRoleCls}>{message.memberRole}</span>
            )}
            <span className={timestampCls}>{message.time}</span>
          </div>
        )}

        {message.agentSignal && (
          <Badge tone="agent" variant="subtle" size="sm">
            {message.agentSignal}
          </Badge>
        )}

        <div className={bubbleClassName}>
          {content}
          {variant === 'agent' && (
            <span className={watermarkCls} aria-hidden="true">
              AGENT · {message.memberName}
            </span>
          )}
        </div>
      </div>
    </article>
  );
}
MessageBubble.displayName = 'MessageBubble';
